import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useContext,
  useLayoutEffect,
} from "react";
import Spin from "components/spin/spin";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-light-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import "./style.scss";
import { ButtonNew as Button } from "components/buttonNew";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import Portal from "elements/Portal";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { SheetLayerContext } from "components/sheet/SheetLayerContext";

export interface ISelectProps {
  prefixCls?: string;
  className?: string;
  block?: boolean;
  defaultValue?: undefined | string | number;
  onChange?: (value: any, extraValues: any) => void;
  showSearch?: boolean;
  placeholder?: string;
  onSearch?: any;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  label?: string;
  labelAction?: { content: any; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void };
  ref?: any;
  allowClear?: boolean;
  searchValue?: string;
  noData?: React.ReactElement;
  dropDownPositionTop?: boolean;
  /** Keep the menu under the field. Skips the flip that opens it above when space below is tight. */
  dropDownPositionBottom?: boolean;
  inputDropdown?: boolean;
  disabled?: boolean;
  smallDropdownSize?: boolean;
  showDropDownOnFocus?: boolean;
  searching?: boolean;
  hideDropdown?: boolean;
  error?: boolean;
  helpText?: string;
  /**Render dropdown outside root DOM using <Portal> component. Set to true for dropdowns used inside modals that overflow */
  usePortal?: boolean;
  portalId?: string;
  handleClear?: any;
  autoFocus?: boolean;
  timeSelect?: boolean;
  customerSearch?: boolean;
  showPlaceholder?: boolean;
  id?: string;
  dropdownClassName?: string;
  /** Minimum width (px) for portaled dropdown menus; expands beyond the trigger width when needed */
  dropdownMinWidth?: number;
}

export interface ISelectState {
  value: string | number;
  dropdownVisible: boolean;
  search: string;
  focus: boolean;
  extraValues: { [key: string]: any };
  hideDropdown: boolean;
}

export const SelectContext = createContext(null);

const Select: React.FC<ISelectProps> = forwardRef((props, ref) => {
  const {
    children,
    prefixCls = "rc-select",
    block,
    className,
    defaultValue = undefined,
    onChange,
    showSearch,
    placeholder = "Placeholder",
    onSearch,
    loading = false,
    leftIcon,
    label,
    labelAction,
    allowClear,
    searchValue = "",
    noData,
    disabled,
    dropDownPositionTop,
    dropDownPositionBottom,
    smallDropdownSize,
    showDropDownOnFocus,
    searching,
    hideDropdown,
    error,
    usePortal,
    portalId,
    autoFocus,
    timeSelect,
    customerSearch,
    showPlaceholder,
    id,
    dropdownClassName,
    dropdownMinWidth,
  } = props;

  const sheetLayer = useContext(SheetLayerContext);
  // Menus inside a stacked sheet stay above that sheet (500).
  // Menus on the sheet underneath stay below the stacked backdrop (400)
  // so player search results cannot cover a sheet opened on top.
  const portaledDropdownZIndex = sheetLayer > 0 ? 510 : 115;

  const [state, setState] = useState<ISelectState>({
    value: defaultValue,
    dropdownVisible: false,
    hideDropdown: hideDropdown,
    search: searchValue,
    focus: false,
    extraValues: null,
  });

  const updateState = (newState: Partial<ISelectState>) => {
    setState((cur: ISelectState) => {
      return { ...cur, ...newState };
    });
  };

  const positionTopRef = useRef(null);
  const [offSet, setOffset] = useState("67px");
  const [displayClass, setDisplayClass] = useState("rc-select-top-position");

  const [finishedTyping, setFinishedTyping] = useState<boolean>(true);

  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectPosition, setSelectPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
    openAbove: boolean;
  }>({ top: 0, left: 0, maxHeight: 370, openAbove: false });
  const [autoOpenAbove, setAutoOpenAbove] = useState(false);
  const scrollableParentElementRef = useRef<HTMLElement>(null);

  const preferredDropdownMaxHeight = smallDropdownSize ? 188 : 370;
  const menuGap = 8;
  const screenEdgePadding = 24;

  const updatePosition = useCallback(() => {
    const el = selectRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const viewportTop = window.visualViewport?.offsetTop ?? 0;
    const spaceBelow = viewportTop + viewportHeight - rect.bottom - menuGap;
    const spaceAbove = rect.top - viewportTop - menuGap;
    const openAbove =
      !dropDownPositionBottom &&
      (!!dropDownPositionTop || (spaceBelow < preferredDropdownMaxHeight && spaceAbove > spaceBelow));
    const availableSpace = openAbove ? spaceAbove : spaceBelow - screenEdgePadding;
    const maxHeight = Math.max(Math.min(preferredDropdownMaxHeight, availableSpace), 80);

    if (openAbove) {
      setSelectPosition({
        top: undefined,
        bottom: viewportTop + viewportHeight - rect.top + menuGap,
        left: rect.left,
        maxHeight,
        openAbove: true,
      });
    } else {
      setSelectPosition({
        top: rect.bottom + menuGap,
        bottom: undefined,
        left: rect.left,
        maxHeight,
        openAbove: false,
      });
    }
    setOffset(`${rect.bottom}px`);
    setAutoOpenAbove(openAbove);
  }, [dropDownPositionBottom, dropDownPositionTop, preferredDropdownMaxHeight]);

  useLayoutEffect(() => {
    if (!usePortal) {
      return;
    }
    if (state.dropdownVisible) {
      updatePosition();
    } else {
      setAutoOpenAbove(false);
    }
  }, [state.dropdownVisible, updatePosition, usePortal]);

  useEffect(() => {
    if (!usePortal) {
      return;
    }

    let scrollParent: HTMLElement = null;
    if (selectRef.current) {
      scrollParent = getOverflowParent(selectRef.current);
    }

    const handleScroll = () => updatePosition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    if (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
      scrollableParentElementRef.current = scrollParent;
    } else {
      scrollableParentElementRef.current = null;
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (scrollableParentElementRef.current) {
        scrollableParentElementRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [usePortal, updatePosition]);

  // Handle API/Promised based setting of defaultValue
  useEffect(() => {
    let mounted = true;

    if (defaultValue) {
      if (mounted) {
        updateState({ value: defaultValue });
      }
    }

    return () => {
      mounted = false;
    };
  }, [defaultValue]);

  useEffect(() => {
    if (autoFocus) {
      handleFocus();
    }
  }, [autoFocus]);

  const handleFocus = () => {
    focusInput();
    if (showDropDownOnFocus) {
      updateState({ focus: true, dropdownVisible: true });
    } else {
      updateState({ focus: true });
    }
  };

  const handleBlur = () => {
    updateState({ focus: false });
  };

  const handleInputChange = (e: any) => {
    updateState({ search: e.target.value });
  };

  //Clear search via ref
  useImperativeHandle(ref, () => ({
    clearSearch() {
      updateState({ search: "" });
    },
  }));

  const cls = classNames(prefixCls, className, {
    [`${prefixCls}-block`]: block,
    [`${prefixCls}-hasSearch`]: showSearch,
    [`${prefixCls}-disabled`]: disabled,
    [`${prefixCls}-error`]: error,
  });

  const handleSelect = () => {
    if (!showSearch) {
      updateState({ dropdownVisible: !state.dropdownVisible });
    }

    if (showSearch) {
      inputRef.current.focus();
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const blurInput = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const mappedChildren = React.Children.map(children, child => {
    return child;
  });

  useEffect(() => {
    setFinishedTyping(false);
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = setTimeout(() => {
        if (mounted) {
          setFinishedTyping(true);
        }
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(Number(timeoutId));
    };
  }, [searchValue]);

  const childNode =
    mappedChildren?.length >= 1 ? (
      <ul className="rc-select-dropdown-list">{mappedChildren}</ul>
    ) : (
      noData && searchValue.length > 0 && finishedTyping === true && !searching && noData
    );

  const emptyValue = state.value === undefined || state.value === null || state.value === "";
  const hasClearableContent = showSearch ? state.search?.length > 0 || !emptyValue : !emptyValue;

  useEffect(() => {
    let mounted = true;
    if (!usePortal && state.dropdownVisible && positionTopRef.current) {
      const triggerEl = (selectRef.current || positionTopRef.current.parentElement) as HTMLElement | null;
      const dropdownEl = positionTopRef.current.children[0] as HTMLElement | undefined;
      if (!triggerEl || !dropdownEl) {
        return;
      }

      const rect = triggerEl.getBoundingClientRect();
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const clientHeight = dropdownEl.clientHeight || preferredDropdownMaxHeight;
      const openAbove =
        !dropDownPositionBottom &&
        (!!dropDownPositionTop || (spaceBelow < clientHeight && spaceAbove > spaceBelow));

      if (mounted) {
        setAutoOpenAbove(openAbove);
        if (openAbove) {
          setOffset(`-${clientHeight + gap}px`);
          setTimeout(() => {
            if (mounted) {
              setDisplayClass("rc-select-top-position-visible");
            }
          }, 100);
        } else {
          setOffset("0");
          setDisplayClass("rc-select-top-position");
        }
      }
    }
    return () => {
      mounted = false;
    };
  }, [childNode, dropDownPositionBottom, dropDownPositionTop, preferredDropdownMaxHeight, state.dropdownVisible, usePortal]);

  const renderSelected = () => {
    if (showSearch) {
      return (
        <>
          <FontAwesomeIcon
            className="rc-select-dropdown-icon"
            icon={["far", customerSearch ? "user" : "magnifying-glass"]}
          />

          <input
            ref={inputRef}
            type="text"
            onChange={handleInputChange}
            value={state.search}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={classNames("rc-select-search text-black font-medium w-full", {
              "rc-select-search_disabled": disabled,
            })}
            disabled={disabled}
            autoFocus={state.focus}
            id={id}
          />
        </>
      );
    } else {
      if ((!state.value || state.value === "") && showPlaceholder) {
        return (
          <span className="rc-select-placeholder">
            {leftIcon}
            {placeholder}
          </span>
        );
      }
      return React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return (
            <>
              {child.type === React.Fragment
                ? React.Children.map(child.props.children, fragmentChild => {
                    return (
                      fragmentChild.props.value &&
                      fragmentChild.props.value === state.value && (
                        <span className="rc-select-dropdown-list-item-selected-text">
                          {leftIcon}
                          <span
                            className={
                              props.inputDropdown ? "whitespace-nowrap overflow-hidden overflow-ellipsis w-24" : ""
                            }
                          >
                            {fragmentChild.props.children}
                          </span>
                        </span>
                      )
                    );
                  })
                : child.props.value === state.value && (
                    <span className="rc-select-dropdown-list-item-selected-text">
                      {leftIcon}
                      <span
                        className={
                          props.inputDropdown ? "whitespace-nowrap overflow-hidden overflow-ellipsis w-24" : ""
                        }
                      >
                        {child.props.children}
                      </span>
                    </span>
                  )}
            </>
          );
        }
      });
    }
  };

  useEffect(() => {
    let mounted = true;

    if (onChange) {
      if (state.value !== undefined && state.value !== null) {
        if (mounted) {
          onChange(state.value, state.extraValues);
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [state.value]);

  useEffect(() => {
    let mounted = true;

    if (onSearch) {
      if (mounted) {
        if (state.search !== undefined) {
          onSearch(state.search);
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [state.search]);

  useEffect(() => {
    let mounted = true;

    if (showSearch) {
      updateState({ search: searchValue });

      if (emptyValue) {
        if (searchValue.length > 0 && state.dropdownVisible === false) {
          if (mounted) {
            if (!state.hideDropdown) {
              updateState({ dropdownVisible: true });
            } else {
              updateState({ hideDropdown: false });
            }
          }
        }
      }

      if (searchValue.length === 0 && children && state.focus === true) {
        if (mounted) {
          updateState({
            dropdownVisible: true,
            value: undefined,
            search: "",
          });
        }
      } else if (searchValue.length === 0 && state.dropdownVisible === true) {
        if (state.hideDropdown === undefined) {
          if (mounted) {
            updateState({ dropdownVisible: false });
          }
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [searchValue]);

  // useEffect(() => {
  //   let mounted = true;

  //   if (showSearch) {
  //     updateState({ search: searchValue });

  //     if (emptyValue) {
  //       if (searchValue.length > 0 && state.dropdownVisible === false) {
  //         if (mounted) {
  //           if (!state.hideDropdown) {
  //             updateState({ dropdownVisible: true });
  //           } else {
  //             updateState({ hideDropdown: false });
  //           }
  //         }
  //       }
  //     }

  //     if (searchValue.length === 0 && state.dropdownVisible === true) {
  //       if (state.hideDropdown === undefined) {
  //         if (mounted) {
  //           updateState({ dropdownVisible: false });
  //         }
  //       }
  //     }

  //     if (searchValue.length === 0) {
  //       if (mounted) {
  //         updateState({ value: undefined, search: "" });
  //       }
  //     }
  //   }

  //   return () => {
  //     mounted = false;
  //   };
  // }, [searchValue]);

  let clearNode: React.ReactNode;

  const onClear = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation();
    if (props.handleClear) {
      props.handleClear();
    }
    updateState({ value: undefined });

    if (showSearch) {
      updateState({ search: "" });
      focusInput();
    }
  };

  if (allowClear) {
    clearNode = (
      <span
        onClick={e => onClear(e)}
        className={classNames(`${prefixCls}-clear`, { [`${prefixCls}-clear-visible`]: hasClearableContent })}
      >
        <FontAwesomeIcon icon={faTimes} size="1x" />
      </span>
    );
  }

  const searchIcon = loading && <Spin />;

  const arrowNode =
    props.inputDropdown && !props.disabled ? (
      <span
        className={classNames(`${prefixCls}-arrow-input`, {
          [`${prefixCls}-hide`]: allowClear && hasClearableContent,
        })}
      >
        {showSearch ? (
          searchIcon
        ) : (
          <FontAwesomeIcon
            icon={timeSelect ? faClock : state.dropdownVisible ? faChevronUp : faChevronDown}
            className="rc-select-dropdown-icon"
          />
        )}
      </span>
    ) : (
      <span
        className={classNames(`${prefixCls}-arrow`, {
          [`${prefixCls}-hide`]: allowClear && hasClearableContent,
        })}
      >
        {showSearch ? (
          searchIcon
        ) : !props.disabled ? (
          <FontAwesomeIcon
            icon={timeSelect ? faClock : state.dropdownVisible ? faChevronUp : faChevronDown}
            className="rc-select-dropdown-icon"
          />
        ) : undefined}
      </span>
    );

  // Pass stable RefObjects via `refs` — never call the hook's accumulating
  // callback ref from an inline ref (that appends to state every render → loop).
  useOnclickOutside(
    () => {
      if (state.value === undefined && state.focus === true && usePortal === false) {
        updateState({ search: "" });
      }
      updateState({ dropdownVisible: false });
      setDisplayClass("rc-select-top-position");
    },
    { refs: [selectRef, dropdownRef] },
  );

  /**
   * @description Returns the first overflow parent HTML element found from a starting HTML Element
   * @param {HTMLElement} element - Starting HTML Element
   * @returns HTMLElement
   */
  function getOverflowParent(element: HTMLElement): HTMLElement {
    const overflowYStyling = element && window.getComputedStyle(element).overflowY;
    const scrollable = overflowYStyling !== "visible" && overflowYStyling !== "hidden";

    if (!element) {
      return null;
    } else if (scrollable && element.scrollHeight >= element.clientHeight) {
      // check if element has overflow scrollable css property and the element's contents scroll height >= the element's height
      return element;
    }

    return getOverflowParent(element.parentElement) || document.body;
  }

  const DropdownWrapper = useCallback(({ usePortal, children }: { usePortal: boolean; children: React.ReactNode }) => {
    return usePortal ? <Portal parentId={portalId}>{children}</Portal> : <>{children}</>;
  }, [portalId]);

  const triggerWidth = selectRef?.current?.clientWidth ?? 0;
  const portaledDropdownWidth =
    dropdownMinWidth != null ? Math.max(triggerWidth, dropdownMinWidth) : triggerWidth;

  return (
    <SelectContext.Provider value={{ state, updateState, onChange, focusInput, blurInput }}>
      <div className={props.inputDropdown ? "" : "rc-select-container"}>
        {label && (
          <div className="flex flex-wrap justify-between items-end">
            <div className="rc-select-dropdown-label">
              <label>{label}</label>
            </div>
            {labelAction ? (
              <div className="rc-select-dropdown-label">
                <Button className="rc-select-dropdown-label-button" type="link" onClick={labelAction.onClick}>
                  {labelAction.content}
                </Button>
              </div>
            ) : null}
          </div>
        )}
        <div tabIndex={0} className={cls} ref={selectRef} onClick={handleSelect}>
          {renderSelected()}
          {clearNode}
          {arrowNode}
        </div>

        {state.dropdownVisible && !props.disabled && (
          <div
            ref={positionTopRef}
            className={dropDownPositionTop || autoOpenAbove ? displayClass : ""}
            style={{ top: dropDownPositionTop || autoOpenAbove ? offSet : "0" }}
          >
            <DropdownWrapper usePortal={usePortal}>
              <div
                className={classNames(
                  "rc-select-dropdown",
                  dropdownClassName,
                  {
                    [`${prefixCls}-dropdown-lower`]: label && !usePortal && !selectPosition.openAbove && !autoOpenAbove,
                  },
                  smallDropdownSize ? "rc-select-dropdown-small" : "rc-select-dropdown-medium",
                )}
                ref={dropdownRef}
                style={
                  usePortal
                    ? {
                        ...(selectPosition.openAbove
                          ? { top: "auto", bottom: selectPosition.bottom }
                          : { top: selectPosition.top, bottom: "auto" }),
                        left: selectPosition.left ?? selectRef.current?.getBoundingClientRect().left ?? 0,
                        width: portaledDropdownWidth || undefined,
                        zIndex: portaledDropdownZIndex,
                        maxHeight: selectPosition.maxHeight,
                        minHeight: 0,
                        overflowY: "auto",
                        minWidth: "fit-content",
                        position: "fixed",
                        marginTop: 0,
                        boxSizing: "border-box",
                      }
                    : undefined
                }
              >
                {childNode}
                {searching && (
                  <div className="h-8 overflow-hidden">
                    <Spin />
                  </div>
                )}
              </div>
            </DropdownWrapper>
          </div>
        )}

        {props.helpText ? (
          <div className="rc-select-help-text font-normal text-gray-400 mt-2">
            <span>{props.helpText}</span>
          </div>
        ) : null}
      </div>
    </SelectContext.Provider>
  );
});

Select.displayName = "Select";
export default Select;
